import React, { useRef, useState } from 'react';
import { UploadCloud, Loader2, X, ImageOff } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { SafeImage } from './SafeImage';

/**
 * ImageUpload - upload an image to Bunny CDN via /api/uploads/image and
 * report the resulting public URL back via onChange(url).
 *
 * Props:
 *   value:      current image URL (or empty string)
 *   onChange:   (url: string) => void
 *   folder:     'products' | 'business' | 'categories' | 'brands' | 'banners' | 'misc'
 *   label:      optional label above the widget
 *   size:       'sm' | 'md' | 'lg'   (visual preview size)
 *   className:  extra classes for the wrapper
 *   testId:     data-testid prefix
 */
export function ImageUpload({
    value = '', onChange, folder = 'misc', label,
    size = 'md', className = '', testId = 'image-upload',
    accept = 'image/*',
}) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);

    const sizeCls = {
        sm: 'h-16 w-16',
        md: 'h-28 w-28',
        lg: 'h-40 w-40',
    }[size] || 'h-28 w-28';

    const handleFiles = async (files) => {
        if (!files || !files.length) return;
        const file = files[0];
        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Image must be under 10 MB');
            return;
        }
        setUploading(true);
        setProgress(0);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('folder', folder);
            const r = await api.post('/uploads/image', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            const url = r?.data?.url;
            if (!url) throw new Error('No URL returned');
            onChange && onChange(url);
            toast.success('Image uploaded');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
            setProgress(0);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (uploading) return;
        handleFiles(e.dataTransfer.files);
    };

    return (
        <div className={className}>
            {label && <div className="text-sm font-medium mb-1.5">{label}</div>}
            <div className="flex items-start gap-3">
                {/* Preview */}
                <div
                    className={`relative ${sizeCls} rounded-md border border-dashed border-neutral-300 bg-neutral-50 overflow-hidden shrink-0`}
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={onDrop}
                    data-testid={`${testId}-preview`}
                >
                    {value ? (
                        <SafeImage src={value} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <div className="h-full w-full flex flex-col items-center justify-center text-neutral-400 text-[10px] text-center px-1">
                            <ImageOff className="h-5 w-5 mb-1" />
                            No image
                        </div>
                    )}
                    {uploading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/85">
                            <Loader2 className="h-5 w-5 animate-spin text-neutral-700" />
                            <div className="text-[10px] mt-1 text-neutral-600">{progress}%</div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={uploading}
                            onClick={() => inputRef.current?.click()}
                            data-testid={`${testId}-button`}
                        >
                            {uploading
                                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Uploading…</>
                                : <><UploadCloud className="h-3.5 w-3.5 mr-1.5" />{value ? 'Replace' : 'Upload'} image</>}
                        </Button>
                        {value && !uploading && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => onChange && onChange('')}
                                data-testid={`${testId}-clear`}
                            >
                                <X className="h-3.5 w-3.5 mr-1" />Remove
                            </Button>
                        )}
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-1.5">
                        JPG / PNG / WebP / SVG. Max 10 MB. Drag &amp; drop supported.
                    </div>
                    {value && (
                        <div className="text-[11px] text-neutral-500 mt-1 truncate" title={value}>
                            <span className="font-medium">CDN:</span> {value}
                        </div>
                    )}
                </div>
            </div>

            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid={`${testId}-input`}
            />
        </div>
    );
}

export default ImageUpload;
